import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Space, Row, Input, message } from "antd";
import { Link, useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Card } from "misc/card";
import { TextField } from "misc/fields";
import { clientUrls } from "misc/urls";
import { AppSelector } from "misc/selectors";
import { PermissionsTransfer, OrganisationalUnitTransfer, ManufacturerTransfer } from "misc/transfer";
import { PrimaryButton, Button } from "misc/buttons";
import { getQueryParameter } from "misc/helpfunctions";
import { useCreateRole, useRoles, useUpdateRole, usePermissions } from "misc/api/keycloakApi";

import FormItem from "./steps/FormItem";

const RoleComponent = () => {
    const history = useHistory();
    const location = useLocation();

    const [t] = useTranslation();

    const [form] = Form.useForm();

    const app = Form.useWatch("app", form);
    const { roles } = useRoles();
    const { permissions } = usePermissions();
    const { createRole, isLoading: isCreating } = useCreateRole();
    const { updateRole, isLoading: isUpdating } = useUpdateRole();

    const role = useMemo(() => roles.find(role => role.id === getQueryParameter(location, "roleId")), [roles, location]);

    useEffect(() => {
        if (role) {
            form.setFieldsValue({
                ...role,
                app: role.client,
                name: role.name.replace("role_", ""),
                permissions: role.permissions.filter(permission => permission?.client === app).map(permission => permission.id),
                organisationalUnits: role.permissions
                    .filter(permission => permission.name.startsWith("permission_data_"))
                    .map(permission => permission.name.replace("permission_data_", "")),
                manufacturers: role.permissions
                    .filter(permission => permission.name.startsWith("permission_manufacturer_"))
                    .map(permission => permission.name.replace("permission_manufacturer_", ""))
            });
        }
    }, [role, app, form]);

    const onClickSave = values => {
        const organisationalUnitsPermissionsIds = values?.organisationalUnits
            ? values.organisationalUnits.map(
                  organisationalUnit => permissions.find(permission => permission.name === `permission_data_${organisationalUnit}`)?.id
              )
            : [];
        const manufacturersPermissionsIds = values?.manufacturers
            ? values.manufacturers.map(
                  manufacturer => permissions.find(permission => permission.name === `permission_manufacturer_${manufacturer}`)?.id
              )
            : [];
        const permissionsIds = [...values.permissions, ...organisationalUnitsPermissionsIds, ...manufacturersPermissionsIds];
        const payload = {
            client: values.app,
            description: values.description,
            name: values.name,
            permissions: permissionsIds
        };

        role
            ? updateRole(
                  { ...role, ...payload },
                  {
                      onSuccess: () => {
                          message.success(t("t_roles_update_success"), 1, () => history.push(clientUrls.roles.baseUrl()));
                      },
                      onError: error => {
                          return message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
                      }
                  }
              )
            : createRole(payload, {
                  onSuccess: () => {
                      message.success(t("t_roles_create_success"), 1, () => history.push(clientUrls.roles.baseUrl()));
                  },
                  onError: error => {
                      return message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
                  }
              });
    };

    return (
        <>
            <Card title="t_roles_create_title">
                <Space direction="vertical" size={20} style={{ width: "100%" }}>
                    <FormItem
                        form={form}
                        name="app"
                        title="t_roles_create_form_app"
                        //initialValue={role?.client}
                        icon={<FontAwesomeIcon icon={["fas", "grid-2"]} />}
                        rules={[
                            {
                                required: true,
                                message: t("t_form_required_field")
                            }
                        ]}
                    >
                        <AppSelector />
                    </FormItem>

                    <FormItem
                        form={form}
                        name="name"
                        title="t_roles_create_form_name"
                        //initialValue={role?.name && role.name.replace("role_", "")}
                        icon="Aa"
                        rules={[
                            {
                                required: true,
                                message: t("t_form_required_field")
                            },
                            {
                                message: t("t_roles_name_already_taken"),
                                validator: (_, currentValue) => {
                                    return roles.some(
                                        role =>
                                            role.name.replace("role_", "") === currentValue &&
                                            role?.client === app &&
                                            role.id !== getQueryParameter(location, "roleId")
                                    )
                                        ? Promise.reject()
                                        : Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input placeholder={t("t_roles_create_form_name_placeholder")} />
                    </FormItem>

                    <FormItem
                        form={form}
                        name="description"
                        title="t_roles_create_form_description"
                        //initialValue={role?.description}
                        icon={<FontAwesomeIcon icon={["fas", "pen-field"]} />}
                        rules={[
                            {
                                required: true,
                                message: t("t_form_required_field")
                            }
                        ]}
                    >
                        <Input placeholder={t("t_roles_create_form_description_placeholder")} />
                    </FormItem>

                    <FormItem
                        form={form}
                        name="permissions"
                        title="t_roles_create_form_permissions"
                        icon={<FontAwesomeIcon icon={["fas", "lock"]} />}
                        rules={[
                            {
                                required: true,
                                message: t("t_form_required_field")
                            }
                        ]}
                    >
                        <PermissionsTransfer
                            client={app || "empty"}
                            permissionPrefix={app === "analysisframework" ? "permission_module_" : app === "viisi" ? "permission_" : ""}
                        />
                    </FormItem>

                    <FormItem
                        form={form}
                        name="organisationalUnits"
                        title="t_role_organisational_units"
                        icon={<FontAwesomeIcon icon={["fas", "sitemap"]} />}
                        visible={app === "analysisframework"}
                        rules={[
                            {
                                required: true,
                                message: t("t_form_required_field")
                            }
                        ]}
                    >
                        <OrganisationalUnitTransfer />
                    </FormItem>

                    <FormItem
                        form={form}
                        name="manufacturers"
                        title="t_role_manufacturers"
                        icon={<FontAwesomeIcon icon={["fas", "sitemap"]} />}
                        visible={app === "analysisframework"}
                        rules={[
                            {
                                required: true,
                                message: t("t_form_required_field")
                            }
                        ]}
                    >
                        <ManufacturerTransfer />
                    </FormItem>

                    <Row type="flex" justify="end">
                        <Form form={form} onFinish={onClickSave}>
                            <Form.Item>
                                <Space direction="horizontal" size={3}>
                                    <Link to={clientUrls.roles.baseUrl()}>
                                        <Button>
                                            <TextField value="t_cancel" />
                                        </Button>
                                    </Link>
                                    <PrimaryButton htmlType="submit" loading={isCreating || isUpdating}>
                                        <TextField inline value="t_save" />
                                    </PrimaryButton>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Row>
                </Space>
            </Card>
        </>
    );
};

export default RoleComponent;
